import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

// Events for an open ticket panel (ticket channel) and for the board (project-wide).
const TICKET_EVENTS = new Set(['ticket_updated', 'comment_added', 'agent_step']);
const BOARD_EVENTS = new Set(['ticket_updated', 'ticket_trashed', 'ticket_purged', 'project_updated']);

const RealtimeContext = createContext({ subscribe: () => () => {}, connected: false });

const buildWsUrl = () => {
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  const url = new URL(backend);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = '/api/ws';
  return url.toString();
};

export const RealtimeProvider = ({ children, token }) => {
  const wsRef = useRef(null);
  const subsRef = useRef(new Map()); // ticket_id -> Set<callback>
  const projectSubsRef = useRef(new Map()); // project_id -> Set<callback>
  const projectListenersRef = useRef(new Set());
  const [connected, setConnected] = useState(false);

  const send = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(obj));
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    let pingInterval = null;
    let reconnectTimer = null;
    let closedByUs = false;

    const connect = () => {
      const ws = new WebSocket(buildWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        ws.send(JSON.stringify({ type: 'auth', token }));
        // Re-subscribe to all currently tracked ticket ids
        for (const tid of subsRef.current.keys()) {
          ws.send(JSON.stringify({ type: 'subscribe', ticket_id: tid }));
        }
        // Re-subscribe to all currently tracked project ids (chat)
        for (const pid of projectSubsRef.current.keys()) {
          ws.send(JSON.stringify({ type: 'subscribe_project', project_id: pid }));
        }
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
        }, 25000);
      };

      ws.onmessage = (e) => {
        let msg;
        try { msg = JSON.parse(e.data); } catch { return; }
        if (TICKET_EVENTS.has(msg.type)) {
          const tid = msg.ticket_id || msg.payload?.id;
          if (tid && subsRef.current.has(tid)) {
            for (const cb of subsRef.current.get(tid)) cb(msg);
          }
        }
        if (BOARD_EVENTS.has(msg.type)) {
          for (const cb of projectListenersRef.current) cb(msg);
        }
        if (msg.type === 'chat_message' || msg.type === 'assistant_typing') {
          const pid = msg.project_id || msg.payload?.project_id;
          if (pid && projectSubsRef.current.has(pid)) {
            for (const cb of projectSubsRef.current.get(pid)) cb(msg);
          }
        }
      };

      ws.onclose = () => {
        setConnected(false);
        clearInterval(pingInterval);
        if (!closedByUs) {
          reconnectTimer = setTimeout(connect, 1500);
        }
      };

      ws.onerror = () => { /* close handler will reconnect */ };
    };

    connect();

    return () => {
      closedByUs = true;
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, [token]);

  const subscribe = useCallback((ticketId, cb) => {
    if (!subsRef.current.has(ticketId)) {
      subsRef.current.set(ticketId, new Set());
      send({ type: 'subscribe', ticket_id: ticketId });
    }
    subsRef.current.get(ticketId).add(cb);
    return () => {
      const set = subsRef.current.get(ticketId);
      if (!set) return;
      set.delete(cb);
      if (set.size === 0) {
        subsRef.current.delete(ticketId);
        send({ type: 'unsubscribe', ticket_id: ticketId });
      }
    };
  }, [send]);

  const onAny = useCallback((cb) => {
    projectListenersRef.current.add(cb);
    return () => projectListenersRef.current.delete(cb);
  }, []);

  const subscribeProject = useCallback((projectId, cb) => {
    if (!projectSubsRef.current.has(projectId)) {
      projectSubsRef.current.set(projectId, new Set());
      send({ type: 'subscribe_project', project_id: projectId });
    }
    projectSubsRef.current.get(projectId).add(cb);
    return () => {
      const set = projectSubsRef.current.get(projectId);
      if (!set) return;
      set.delete(cb);
      if (set.size === 0) {
        projectSubsRef.current.delete(projectId);
        send({ type: 'unsubscribe_project', project_id: projectId });
      }
    };
  }, [send]);

  return (
    <RealtimeContext.Provider value={{ subscribe, subscribeProject, onAny, connected }}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => useContext(RealtimeContext);
